import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';

import Editor, { Monaco, OnMount } from '@monaco-editor/react';
import MDEditor from '@uiw/react-md-editor';

import { EditorViewMode } from 'components/editor/ViewModeToggle';
import { KnzEditor } from 'components/knz/KnzEditor';

import { MarkdownToolbar } from './MarkdownToolbar';

type CodeEditor = Parameters<OnMount>[0];

type Props = {
  /** Monaco Editor に設定する language（拡張子マスタの editorLanguage） */
  language: string;
  value: string;
  /** md / knz の表示モード（それ以外は 'edit' 固定で渡ってくる） */
  viewMode: EditorViewMode;
  /** md / knz（表示モードを持つ）か */
  supportsViewMode: boolean;
  /** 編集不可（他人のワークスペースでデフォルト編集不可） */
  readOnly: boolean;
  onChange: (value: string) => void;
  /** 表示モードを変更する */
  onViewModeChange: (mode: EditorViewMode) => void;
  /** Cmd/Ctrl+S で保存 */
  onSave?: () => void;
  /** Cmd/Ctrl+E で実行 */
  onRun?: () => void;
  /** knz: CodeBlock link 参照の定義ジャンプ時に、その fileId のファイルを開く */
  onOpenLinkedFile?: (fileId: string) => void;
  /** knz 記事ヘッダー用のファイル日時 */
  createdAt?: string;
  updatedAt?: string;
};

/**
 * 右ペインのエディタ領域。
 * - knz: KnzEditor に委譲
 * - md: エディタ / 分割 / プレビュー の 3 モード（ツールバー付き）
 * - その他: Monaco Editor（編集のみ）
 */
const EditorArea: React.FC<Props> = ({
  language,
  value,
  viewMode,
  supportsViewMode,
  readOnly,
  onChange,
  onViewModeChange,
  onSave,
  onRun,
  onOpenLinkedFile,
  createdAt,
  updatedAt,
}) => {
  const { mode, systemMode } = useColorScheme();
  const resolved = mode === 'system' ? systemMode : mode;
  const isDark = resolved === 'dark';

  const editorRef = React.useRef<CodeEditor | null>(null);
  const isMd = language === 'markdown';

  // md ツールバーの表示/非表示
  const [toolbarVisible, setToolbarVisible] = React.useState(true);

  // プレビュー再描画を軽くするためのデバウンス値
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), 200);
    return () => clearTimeout(timer);
  }, [value]);

  const onSaveRef = React.useRef(onSave);
  const onRunRef = React.useRef(onRun);
  const viewModeRef = React.useRef(viewMode);
  const onViewModeChangeRef = React.useRef(onViewModeChange);
  const supportsRef = React.useRef(supportsViewMode);
  React.useEffect(() => {
    onSaveRef.current = onSave;
    onRunRef.current = onRun;
    viewModeRef.current = viewMode;
    onViewModeChangeRef.current = onViewModeChange;
    supportsRef.current = supportsViewMode;
  }, [onSave, onRun, viewMode, onViewModeChange, supportsViewMode]);

  /** Cmd/Ctrl+P: プレビュー切替（edit⇄preview、split は preview へ） */
  const togglePreviewMode = React.useCallback(() => {
    if (!supportsRef.current) return;
    const current = viewModeRef.current;
    onViewModeChangeRef.current(current === 'preview' ? 'edit' : 'preview');
  }, []);

  /** Cmd/Ctrl+\\: 分割トグル（split⇄edit） */
  const toggleSplitMode = React.useCallback(() => {
    if (!supportsRef.current) return;
    const current = viewModeRef.current;
    onViewModeChangeRef.current(current === 'split' ? 'edit' : 'split');
  }, []);

  const handleMount = React.useCallback<OnMount>(
    (editor, monaco) => {
      editorRef.current = editor;
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => onSaveRef.current?.());
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyE, () => onRunRef.current?.());
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyP, () => togglePreviewMode());
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Backslash, () => toggleSplitMode());
    },
    [togglePreviewMode, toggleSplitMode],
  );

  // プレビュー単独モード時は Monaco が無いので window でショートカットを拾う
  React.useEffect(() => {
    if (!supportsViewMode || viewMode !== 'preview') return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        togglePreviewMode();
      } else if (e.key === '\\') {
        e.preventDefault();
        toggleSplitMode();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [supportsViewMode, viewMode, togglePreviewMode, toggleSplitMode]);

  const handleBeforeMount = React.useCallback((monaco: Monaco) => {
    monaco.editor.defineTheme('workspace-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'minimap.dropShadow': '#00000000',
        'scrollbar.shadow': '#00000000',
        'widget.shadow': '#00000000',
      },
    });
    monaco.editor.defineTheme('workspace-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'minimap.dropShadow': '#00000000',
        'scrollbar.shadow': '#00000000',
        'widget.shadow': '#00000000',
      },
    });
  }, []);

  // knz は専用エディタへ委譲（全 Hooks の後で分岐）
  if (language === 'knz') {
    return (
      <KnzEditor
        value={value}
        viewMode={viewMode}
        readOnly={readOnly}
        onChange={onChange}
        onViewModeChange={onViewModeChange}
        onSave={onSave}
        onOpenLinkedFile={onOpenLinkedFile}
        createdAt={createdAt}
        updatedAt={updatedAt}
      />
    );
  }

  // --- Monaco エディタ本体 ---
  const monacoEditor = (
    <Editor
      height="100%"
      language={language}
      value={value}
      theme={isDark ? 'workspace-dark' : 'workspace-light'}
      beforeMount={handleBeforeMount}
      onMount={handleMount}
      onChange={(v) => onChange(v ?? '')}
      options={{
        readOnly,
        minimap: { enabled: true, renderCharacters: true },
        fontSize: 13,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        wordWrap: isMd ? 'on' : 'off',
      }}
    />
  );

  // --- md プレビュー ---
  const mdPreview = (
    <Box
      data-color-mode={isDark ? 'dark' : 'light'}
      sx={{ height: '100%', overflow: 'auto', p: 2, bgcolor: 'background.paper' }}
    >
      <MDEditor.Markdown source={debouncedValue} style={{ background: 'transparent' }} />
    </Box>
  );

  // md 以外（java 等）はエディタのみ
  if (!isMd) {
    return <Box sx={{ height: '100%', minHeight: 0 }}>{monacoEditor}</Box>;
  }

  // --- md: ツールバー + 3 モード ---
  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {toolbarVisible ? (
        <MarkdownToolbar
          getEditor={() => editorRef.current}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          onHide={() => setToolbarVisible(false)}
        />
      ) : (
        <Tooltip title="ツールバーを表示">
          <Box
            onClick={() => setToolbarVisible(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              cursor: 'pointer',
              borderBottom: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">ツールバー</Typography>
          </Box>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {viewMode !== 'preview' && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              borderRight: viewMode === 'split' ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            {monacoEditor}
          </Box>
        )}
        {viewMode !== 'edit' && <Box sx={{ flex: 1, minWidth: 0 }}>{mdPreview}</Box>}
      </Box>
    </Box>
  );
};

export default EditorArea;
